import * as THREE from 'three';
import AssetManager from '../managers/AssetManager';
import socketService from '../SocketService';
import EventBus from '../EventBus';
import { GameplayUI } from '../ui/GameplayUI';
import { enableMouseLock } from '../core/Controls';
import { applySkinToObject, getTextureForSkin } from '../utils/skin';
import { logDebug } from '../../utils/logger';
import { createShieldMaterial, HIT_POINTS_NUM } from '../materials/ShieldMaterial';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export default class TankModel {
  camera: THREE.Camera | null;
  scene: THREE.Scene;
  textureMap: any;
  turret: THREE.Object3D | null;
  cannon: THREE.Object3D | null;
  tankModel: THREE.Object3D;
  currentMoveSpeed: number;
  currentRotateSpeed: number;
  maxMoveSpeed: number;
  moveAcceleration: number;
  moveDeceleration: number;
  maxRotateSpeed: number;
  rotationAcceleration: number;
  rotationDeceleration: number;
  moveForward: number;
  rotate: number;
  canFire: boolean;
  visualEffectHelper: any;
  projectileSystem: any;
  shieldMesh?: THREE.Mesh | null;
  shieldActive?: boolean;
  shieldRings?: Array<{ position: THREE.Vector3; age: number }>;
  lastMoveDirection: number;
  velX: number;
  turbo: number;
  maxTurretSpeed: number;
  reloadTimeMs: number;
  healtPoints: number;
  _lastUpdateSentAt: number;
  boxHelper?: THREE.BoxHelper | null;
  turretBox?: THREE.BoxHelper | null;

  constructor(scene: THREE.Scene, visualEffectHelper: any, projectileSystem: any = null) {
    this.camera = null;
    this.scene = scene;
    this.textureMap = AssetManager.getTexture('textureA');
    this.turret = null;
    this.cannon = null;
    this.tankModel = new THREE.Object3D();
    this.currentMoveSpeed = 0;
    this.currentRotateSpeed = 0;
    this.maxMoveSpeed = 30;
    this.moveAcceleration = 15;
    this.moveDeceleration = 10;
    this.maxRotateSpeed = (Math.PI / 180) * 120;
    this.rotationAcceleration = 10;
    this.rotationDeceleration = 15;
    this.moveForward = 0;
    this.rotate = 0;
    this.canFire = true;
    this.visualEffectHelper = visualEffectHelper;
    this.projectileSystem = projectileSystem;
    this.shieldMesh = null;
    this.shieldActive = false;
    this.shieldRings = [];

    this.lastMoveDirection = 0;

    this.velX = 0;
    this.turbo = 0;

    this.maxTurretSpeed = Math.PI / 2;

    this.reloadTimeMs = 0.2 * 1000;
    logDebug('TankModel: constructing');

    this.healtPoints = 100;

    // Throttle network updates
    this._lastUpdateSentAt = 0;
  }

  addToScene(showWireframe: boolean = false, showBoundingBox: boolean = false) {
    this.tankModel = AssetManager.getModel('tankModel') as THREE.Object3D;
    this.boxHelper = null;
    this.turretBox = null;

    this.tankModel.traverse((child: THREE.Object3D) => {
      if (child.name === 'Main_Gun') {
        this.cannon = child;
      }
      if (child.name === 'TopPart') {
        this.turret = child;
        (this.turret as any).userData.info = {
          name: 'tankTurret',
          description: 'A 3D tank turret.',
          collision: true,
        };
      }
      if ((child as any).isMesh) {
        const mesh = child as unknown as THREE.Mesh;
        (mesh as any).receiveShadow = true;
        (mesh as any).castShadow = true;
        const tankMaterial = new THREE.MeshStandardMaterial({
          color: 0xaaaaaa,
          roughness: 0.5,
          metalness: 0.3,
          wireframe: showWireframe,
        });
        mesh.material = tankMaterial;
        (mesh.material as any).map = this.textureMap;
      }
    });

    this.tankModel.scale.set(0.01, 0.01, 0.01);
    this.tankModel.position.set(0, 1, 0);
    this.tankModel.rotation.set(0, 0, 0);

    (this.tankModel as any).userData.info = {
      name: 'MyTankModel',
      description: 'My local character',
      collision: true,
    };

    this.scene.add(this.tankModel);
    logDebug('TankModel: added tank to scene');

    try {
      const worldBox = new THREE.Box3().setFromObject(this.tankModel);
      const size = new THREE.Vector3();
      worldBox.getSize(size);
      const center = new THREE.Vector3();
      worldBox.getCenter(center);
      const objCenter = new THREE.Vector3();
      this.tankModel.getWorldPosition(objCenter);
      (this.tankModel as any).userData._collider = {
        type: 'sphere',
        radius: size.length() * 0.35,
        offset: center.sub(objCenter),
      };
      // Pre-create shield mesh with radius slightly larger than tank collider
      const shieldRadius = ((this.tankModel as any).userData._collider.radius || 3) * 1.2;
      const shieldGeo = new THREE.SphereGeometry(shieldRadius, 64, 32);
      const shieldMat = createShieldMaterial();
      const setupShieldUserData = (shieldObj: THREE.Object3D) => {
        (shieldObj as any).name = 'Shield';
        // Ensure material applied to all meshes inside
        (shieldObj as any).traverse?.((o: any) => { if (o.isMesh) o.material = shieldMat; });
        (shieldObj as any).userData.info = { type: 'shield', owner: this.tankModel };
        (shieldObj as any).userData._collider = { type: 'sphere', radius: shieldRadius, offset: new THREE.Vector3(0, 0, 0) };
        (shieldObj as any).userData.addShieldHit = (hitPoint: THREE.Vector3) => {
          if (!this.shieldRings) this.shieldRings = [];
          this.shieldRings.push({ position: hitPoint.clone(), age: 0 });
          if (this.shieldRings.length > HIT_POINTS_NUM) this.shieldRings.shift();
        };
      };

      // Try to load demo shield model; fallback to sphere
      const loader = new GLTFLoader();
      loader.load(
        '/assets/shield/shield.glb',
        (gltf) => {
          const target = gltf.scene.getObjectByName('Shield') || gltf.scene;
          const shield = (target as any).clone();
          if (!(shield as any).isMesh) {
            let found: any = null;
            target.traverse((o: any) => { if (!found && o.isMesh) found = o; });
            if (found) this.shieldMesh = found;
            else {
              const shieldGeo2 = new THREE.SphereGeometry(shieldRadius, 64, 32);
              this.shieldMesh = new THREE.Mesh(shieldGeo2, shieldMat);
            }
          } else {
            this.shieldMesh = shield as any;
          }
          (this.shieldMesh as any).visible = !!this.shieldActive;
          (this.shieldMesh as any).renderOrder = 10;
          setupShieldUserData(this.shieldMesh as any);
          this.scene.add(this.shieldMesh as any);
          // If shield already active while loaded, ensure it participates in collisions
          try {
            if (this.shieldActive) {
              const envAny = (this.scene as any);
              // Find collidables array if passed around; fallback handled in update
            }
          } catch {}
        },
        undefined,
        () => {
          const shieldGeo2 = new THREE.SphereGeometry(shieldRadius, 64, 32);
          const shield2 = new THREE.Mesh(shieldGeo2, shieldMat);
          shield2.visible = !!this.shieldActive;
          (shield2 as any).renderOrder = 10;
          setupShieldUserData(shield2);
          this.shieldMesh = shield2;
          this.scene.add(shield2);
        }
      );
    } catch (e) {
      // ignore
    }

    if (showBoundingBox) {
      this.boxHelper = new THREE.BoxHelper(this.tankModel, 0xff0000);
      this.scene.add(this.boxHelper);
      if (this.turret) {
        this.turretBox = new THREE.BoxHelper(this.turret, 0x00ff00);
        this.scene.add(this.turretBox);
      }
    }

    EventBus.on('player:kill', () => {
      this.receiveDamage(1, 'suicide');
    });

    GameplayUI.showHealthBar(this.healtPoints, 100);
  }

  updateBoundingBoxes() {
    if (this.boxHelper) this.boxHelper.update();
    if (this.turretBox) this.turretBox.update();
  }

  checkCollisions(collidables: any[]) {
    return this._checkCollisionsOptimized(collidables);
  }

  _checkCollisionsOptimized(collidables: any[]) {
    const selfCol = (this.tankModel as any).userData._collider;
    if (!selfCol) return null;
    const selfCenter = new THREE.Vector3();
    this.tankModel.getWorldPosition(selfCenter);
    if (selfCol.offset) selfCenter.add(selfCol.offset);
    const selfR = selfCol.radius || 1;

    for (const obj of collidables as any[]) {
      if (obj === this.tankModel) continue;
      const col = obj.userData && (obj.userData._collider || this._computeColliderFor(obj));
      if (!col) continue;
      if (col.type === 'sphere') {
        const oc = new THREE.Vector3();
        obj.getWorldPosition(oc);
        if (col.offset) oc.add(col.offset);
        const r = col.radius || 1;
        if (oc.distanceTo(selfCenter) <= r + selfR) return obj;
      } else if (col.type === 'aabb') {
        const worldBox = col.localBox
          ? col.localBox.clone().applyMatrix4(obj.matrixWorld)
          : new THREE.Box3().setFromObject(obj);
        const clamped = new THREE.Vector3(
          Math.max(worldBox.min.x, Math.min(selfCenter.x, worldBox.max.x)),
          Math.max(worldBox.min.y, Math.min(selfCenter.y, worldBox.max.y)),
          Math.max(worldBox.min.z, Math.min(selfCenter.z, worldBox.max.z))
        );
        if (clamped.distanceToSquared(selfCenter) <= selfR * selfR) return obj;
      }
    }
    return null;
  }

  _computeColliderFor(obj: any) {
    const info = obj.userData && obj.userData.info;
    if (info && (info.objectName === 'enemyTankModel' || info.name === 'MyTankModel')) {
      const box = new THREE.Box3().setFromObject(obj);
      const size = new THREE.Vector3();
      box.getSize(size);
      const center = new THREE.Vector3();
      box.getCenter(center);
      const objCenter = new THREE.Vector3();
      obj.getWorldPosition(objCenter);
      const col = { type: 'sphere', radius: size.length() * 0.3, offset: center.sub(objCenter) };
      obj.userData._collider = col;
      return col;
    } else if (obj.geometry) {
      if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
      const col = { type: 'aabb', localBox: obj.geometry.boundingBox.clone() };
      obj.userData._collider = col;
      return col;
    } else {
      obj.userData._collider = { type: 'aabb' };
      return obj.userData._collider;
    }
  }

  updateTurretRotation(deltaTime: number) {
    if (!this.turret) return;
    const clampedMouseVelocityX = Math.max(-this.maxTurretSpeed, Math.min(this.maxTurretSpeed, this.velX));
    const targetRotationX = (this.turret as any).rotation.x + clampedMouseVelocityX * deltaTime;
    (this.turret as any).rotation.x = targetRotationX;
  }

  fire() {
    if (!this.canFire) return;
    this.canFire = false;

    const { position, quaternion } = this.getCanonCoordinates();
    logDebug('TankModel.fire quaternion', quaternion);
    try {
      const weapon = this.projectileSystem && this.projectileSystem.getWeapon ? this.projectileSystem.getWeapon() : undefined;
      socketService.fireProjectile(position, quaternion, weapon);
    } catch {
      socketService.fireProjectile(position, quaternion);
    }

    if (this.projectileSystem) {
      const ignoreFor = this.shieldActive && this.shieldMesh ? (this.shieldMesh as any) : this.tankModel;
      this.projectileSystem.fireProjectile(position, quaternion, ignoreFor);
    }

    setTimeout(() => (this.canFire = true), this.reloadTimeMs);
  }

  update(deltaTime: number, collidables: any[], inputs: any) {
    this.updateInputs(deltaTime, inputs);
    this.sendPlayerUpdate();
    this.updateCamera();
    const prevPosition = this.tankModel.position.clone();
    this.updateMovement(deltaTime, collidables);
    this.updateTurretRotation(deltaTime);

    // Shield activation on hold (Ctrl)
    if (inputs && typeof inputs.shield === 'boolean') {
      if (inputs.shield && !this.shieldActive) {
        this.enableShield(collidables);
      } else if (!inputs.shield && this.shieldActive) {
        this.disableShield(collidables);
      }
    }

    // If shield was loading asynchronously and became available later, ensure visibility/collisions match state
    if (this.shieldMesh) {
      if (this.shieldActive) {
        this.shieldMesh.visible = true;
        if (!collidables.includes(this.shieldMesh)) collidables.push(this.shieldMesh);
      } else {
        this.shieldMesh.visible = false;
        const idx = collidables.indexOf(this.shieldMesh);
        if (idx >= 0) collidables.splice(idx, 1);
      }
    }

    // Optional: gentle pulse when active
    if (this.shieldMesh) {
      const pos = new THREE.Vector3();
      this.tankModel.getWorldPosition(pos);
      this.shieldMesh.position.copy(pos);
      if (this.shieldActive) {
        const s = 1 + 0.03 * Math.sin((performance.now() / 200) % (Math.PI * 2));
        this.shieldMesh.scale.set(s, s, s);
      } else {
        this.shieldMesh.scale.set(1, 1, 1);
      }
      const mat: any = (this.shieldMesh.material as any);
      if (mat && mat.uniforms) {
        mat.uniforms.u_Time.value = (performance.now() % 100000) / 1000.0;
        if (!this.shieldRings) this.shieldRings = [];
        for (let i = 0; i < this.shieldRings.length; i++) this.shieldRings[i].age += deltaTime;
        // thickness fades by 0.4s rise + 0.5s fall; keep ring until 0.9s
        this.shieldRings = this.shieldRings.filter(r => r.age <= 0.9);
        const defaults = new Array(HIT_POINTS_NUM).fill(0).map(() => ({ position: new THREE.Vector3(999,999,999), size: 0, thickness: 0 }));
        const uniformsArray = defaults.slice();
        for (let i = 0; i < Math.min(this.shieldRings.length, HIT_POINTS_NUM); i++) {
          const r = this.shieldRings[i];
          // Demo timeline: size: 0->1 over 1.0s; thickness: 0->0.45 in 0.4s, then 0.45->0 in 0.5s
          const size = Math.min(1.0, r.age / 1.0);
          let thickness = 0.0;
          if (r.age <= 0.4) {
            thickness = 0.45 * (r.age / 0.4);
          } else if (r.age <= 0.9) {
            thickness = 0.45 * (1.0 - (r.age - 0.4) / 0.5);
          } else {
            thickness = 0.0;
          }
          uniformsArray[i] = { position: r.position, size, thickness } as any;
        }
        mat.uniforms.u_HitPoints.value = uniformsArray;
      }
    }

    if (inputs.fire) {
      this.fire();
    }
    const collision = this.checkCollisions(collidables);
    if (collision) {
      this.tankModel.position.copy(prevPosition);
    }
    this.updateBoundingBoxes();
  }

  enableShield(collidables: any[]) {
    if (!this.shieldMesh) return;
    this.shieldActive = true;
    this.shieldMesh.visible = true;
    if (!collidables.includes(this.shieldMesh)) collidables.push(this.shieldMesh);
  }

  disableShield(collidables: any[]) {
    if (!this.shieldMesh) return;
    this.shieldActive = false;
    this.shieldMesh.visible = false;
    const idx = collidables.indexOf(this.shieldMesh);
    if (idx >= 0) collidables.splice(idx, 1);
  }

  updateInputs(_deltaTime: number, inputs: any) {
    this.moveForward = inputs.moveDirection;
    this.rotate = inputs.rotateDirection;
    this.velX = inputs.mouseVelocityX;
    this.turbo = inputs.turbo;
    inputs.mouseVelocityX = 0;
  }

  updateMovement(deltaTime: number, collidables: any[]) {
    let _maxMoveSpeed = this.turbo > 0 ? this.maxMoveSpeed * 5 : this.maxMoveSpeed;

    let moveSpeed = this.calculateSpeed(this.moveForward, this.currentMoveSpeed, _maxMoveSpeed, deltaTime, false);
    let rotateSpeed = this.calculateSpeed(this.rotate, this.currentRotateSpeed, this.maxRotateSpeed, deltaTime, true);
    let moveDirection = Math.sign(moveSpeed);

    if (moveDirection !== 0 && moveDirection !== this.lastMoveDirection) {
      this.rotate = -this.rotate;
    }
    this.lastMoveDirection = moveDirection;

    this.currentMoveSpeed = Math.max(Math.min(moveSpeed, _maxMoveSpeed), -_maxMoveSpeed);
    this.currentRotateSpeed = Math.max(Math.min(rotateSpeed, this.maxRotateSpeed), -this.maxRotateSpeed);

    if (this.currentMoveSpeed !== 0) {
      let distance = this.currentMoveSpeed * deltaTime;
      if (this.moveForward === 0) {
        const deceleration = this.moveDeceleration * deltaTime;
        this.currentMoveSpeed -= deceleration * Math.sign(this.currentMoveSpeed) * 1.5;
        if (Math.abs(this.currentMoveSpeed) < deceleration) {
          this.currentMoveSpeed = 0;
        }
        distance = this.currentMoveSpeed * deltaTime;
      }

      // Avoid noisy frame logging
      const collision = this.checkCollisionWithSlide(collidables, this.moveForward, distance);
      if (collision) {
        this.currentMoveSpeed = 0;
      }
    }

    if (this.currentRotateSpeed !== 0) {
      const angle = this.currentRotateSpeed * deltaTime;
      const originalRotation = this.tankModel.rotation.y;
      this.tankModel.rotateY(angle);
      const collision = this.checkCollisions(collidables);
      if (collision) {
        this.tankModel.rotation.y = originalRotation;
        this.currentRotateSpeed = 0;
      }
    }
  }

  calculateSpeed(direction: number, currentSpeed: number, maxSpeed: number, deltaTime: number, isRotation: boolean) {
    const acceleration = isRotation ? this.rotationAcceleration : this.moveAcceleration;
    const deceleration = isRotation ? this.rotationDeceleration : this.moveDeceleration;

    let targetSpeed = direction * maxSpeed;
    if (!isRotation && this.turbo > 0 && direction > 0) {
      targetSpeed = direction * (maxSpeed * 10);
    }

    let changeOfDirectionBoost = 3;

    if (currentSpeed < targetSpeed) {
      let step = acceleration * deltaTime;
      if (Math.sign(currentSpeed) < 0) {
        step *= changeOfDirectionBoost;
      }
      currentSpeed += step;
      if (currentSpeed > targetSpeed) currentSpeed = targetSpeed;
    } else if (currentSpeed > targetSpeed) {
      let step = deceleration * deltaTime;
      if (Math.sign(currentSpeed) > 0) {
        step *= changeOfDirectionBoost;
      }
      currentSpeed -= step;
      if (currentSpeed < targetSpeed) currentSpeed = targetSpeed;
    }
    return currentSpeed;
  }

  sendPlayerUpdate() {
    if (!this.turret) return;
    const now = typeof performance !== 'undefined' && (performance as any).now ? (performance as any).now() : Date.now();
    if (now - this._lastUpdateSentAt < 10) {
      return;
    }
    this._lastUpdateSentAt = now;
    const tankRotation = new THREE.Quaternion();
    const tankPosition = new THREE.Vector3();

    this.tankModel.getWorldQuaternion(tankRotation);
    this.tankModel.getWorldPosition(tankPosition);

    socketService.playerUpdate({
      position: tankPosition,
      rotation: { x: tankRotation.x, y: tankRotation.y, z: tankRotation.z, w: tankRotation.w },
      turretRotationX: (this.turret as any).rotation.x,
    });
  }

  mountCamera(camera: THREE.Camera) {
    this.camera = camera;
  }

  updateCamera() {
    if (!this.camera) return;
    const { cameraPosition, lookAt } = this.getCameraPosition();
    (this.camera as any).position.copy(cameraPosition);
    (this.camera as any).lookAt(lookAt);
  }

  getCanonCoordinates = (): { position: THREE.Vector3; quaternion: THREE.Quaternion } => {
    const localOffset = new THREE.Vector3(0, 0, 7);
    const worldPosition = (this.turret as any).localToWorld(localOffset.clone());
    const turretWorldQuaternion = new THREE.Quaternion();
    (this.turret as any).getWorldQuaternion(turretWorldQuaternion);
    return { position: worldPosition, quaternion: turretWorldQuaternion.clone() };
  };

  getCameraPosition = (): { cameraPosition: THREE.Vector3; lookAt: THREE.Vector3 } => {
    const tankPosition = this.tankModel.position.clone();
    const cameraOffset = new THREE.Vector3(0, 3, 6);
    cameraOffset.applyQuaternion(this.tankModel.quaternion);
    const turretYawRotation = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), (this.turret as any).rotation.x);
    cameraOffset.applyQuaternion(turretYawRotation);
    let cameraPosition = tankPosition.clone().add(cameraOffset);
    let lookAt = tankPosition.clone().add(new THREE.Vector3(0, 1.5, 0));
    return { cameraPosition, lookAt };
  };

  receiveDamage = (damage: number, attackedId: string) => {
    // Shield blocks incoming damage completely while active
    if (this.shieldActive) {
      // Optional: small visual effect to indicate block
      try {
        this.visualEffectHelper.createExplosion(this.tankModel.position.clone(), 2);
      } catch {}
      return;
    }
    if (this.healtPoints <= 0) {
      console.log('TankModel already dead, exit');
      return;
    }
    console.log('receiveDamage tankmodel', this.healtPoints);
    this.healtPoints -= damage;
    if (this.healtPoints < 0) this.healtPoints = 0;
    GameplayUI.updateHealthBar(this.healtPoints, 100);
    if (this.healtPoints <= 0) {
      console.log('DEAD tankmodel, killer', attackedId);
      this.visualEffectHelper.createExplosion(this.tankModel.position.clone(), 10);
      socketService.playerDead();
      this.deathScreen();
    }
  };

  deathScreen = () => {
    document.body.style.cursor = 'default';
    (document as any).exitPointerLock();
    const overlay = document.createElement('div');
    overlay.id = 'deathOverlay';
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100%';
    overlay.style.height = '100%';
    overlay.style.backgroundColor = 'rgba(0, 0, 0, 0.7)';
    overlay.style.display = 'flex';
    overlay.style.flexDirection = 'column';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '2000';
    overlay.style.pointerEvents = 'auto';
    const text = document.createElement('div');
    text.innerText = 'You are dead';
    text.style.color = 'white';
    text.style.fontSize = '3em';
    text.style.fontFamily = 'Arial, sans-serif';
    text.style.textAlign = 'center';
    text.style.marginBottom = '2rem';
    const btnContainer = document.createElement('div');
    btnContainer.style.display = 'flex';
    btnContainer.style.gap = '1rem';

    const lobbyButton = document.createElement('button');
    lobbyButton.innerText = 'Back to Lobby';
    lobbyButton.style.padding = '1rem 2rem';
    lobbyButton.style.fontSize = '1.2em';
    lobbyButton.style.fontFamily = 'Arial, sans-serif';
    lobbyButton.style.backgroundColor = '#4CAF50';
    lobbyButton.style.color = 'white';
    lobbyButton.style.border = 'none';
    lobbyButton.style.borderRadius = '8px';
    lobbyButton.style.cursor = 'pointer';
    lobbyButton.style.transition = 'background-color 0.3s';
    lobbyButton.addEventListener('click', () => {
      try {
        const el = document.getElementById('deathOverlay');
        if (el && el.parentNode) el.parentNode.removeChild(el);
      } catch {}
      window.location.href = '/';
    });

    const respawnButton = document.createElement('button');
    respawnButton.innerText = 'Respawn (3)';
    respawnButton.disabled = true;
    respawnButton.style.padding = '1rem 2rem';
    respawnButton.style.fontSize = '1.2em';
    respawnButton.style.fontFamily = 'Arial, sans-serif';
    respawnButton.style.backgroundColor = '#2196F3';
    respawnButton.style.color = 'white';
    respawnButton.style.border = 'none';
    respawnButton.style.borderRadius = '8px';
    respawnButton.style.cursor = 'not-allowed';
    respawnButton.style.transition = 'background-color 0.3s, opacity 0.3s';

    let countdown = 3;
    const timer = setInterval(() => {
      countdown -= 1;
      if (countdown <= 0) {
        clearInterval(timer);
        respawnButton.disabled = false;
        respawnButton.style.cursor = 'pointer';
        respawnButton.innerText = 'Respawn';
      } else {
        respawnButton.innerText = `Respawn (${countdown})`;
      }
    }, 1000);

    respawnButton.addEventListener('click', () => {
      if (respawnButton.disabled) return;
      try {
        const el = document.getElementById('deathOverlay');
        if (el && el.parentNode) el.parentNode.removeChild(el);
      } catch {}
      this.respawn();
    });

    overlay.appendChild(text);
    btnContainer.appendChild(lobbyButton);
    btnContainer.appendChild(respawnButton);
    overlay.appendChild(btnContainer);
    document.body.appendChild(overlay);
  };

  respawn = () => {
    // Restore health and UI
    this.healtPoints = 100;
    try { GameplayUI.updateHealthBar(this.healtPoints, 100); } catch {}
    // Do not reset world state or other players; keep current position/rotation
    // Re-enable pointer lock
    try { enableMouseLock(); } catch {}
    // Send immediate state so others see us respawned
    try { this.sendPlayerUpdate(); } catch {}
    // Re-announce to server so other clients re-add us if needed
    try {
      const cookieName = (GameplayUI.getCookie && GameplayUI.getCookie('playerName')) || '';
      const cookieSkin = (GameplayUI.getCookie && GameplayUI.getCookie('tankSkin')) || '';
      const ssName = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('username') || '') : '';
      const ssSkin = typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('selectedSkin') || '') : '';
      const name = cookieName || ssName || 'Player';
      const skin = cookieSkin || ssSkin || 'T90A';
      // Broadcast respawn intent via consolidated game:action so others can re-add us
      socketService.sendAction('player:respawn', { name, color: skin });
    } catch {}
  };

  checkCollisionWithSlide(collidables: any[], direction: number, distance: number) {
    const originalPosition = this.tankModel.position.clone();
    const originalRotation = this.tankModel.rotation.y;
    this.tankModel.translateZ(-distance);
    let collision = this.checkCollisions(collidables);
    if (collision) {
      this.tankModel.position.copy(originalPosition);
      const slideDistance = Math.abs(distance) * 0.5;
      this.tankModel.translateX(slideDistance);
      collision = this.checkCollisions(collidables);
      if (!collision) {
        this.tankModel.translateZ(-distance);
        collision = this.checkCollisions(collidables);
        if (collision) {
          this.tankModel.position.copy(originalPosition);
          return true;
        }
        return false;
      }
      this.tankModel.position.copy(originalPosition);
      this.tankModel.translateX(-slideDistance);
      collision = this.checkCollisions(collidables);
      if (!collision) {
        this.tankModel.translateZ(-distance);
        collision = this.checkCollisions(collidables);
        if (collision) {
          this.tankModel.position.copy(originalPosition);
          return true;
        }
        return false;
      }
      this.tankModel.position.copy(originalPosition);
      const smallRotation = Math.sign(direction) * 0.1;
      this.tankModel.rotation.y = originalRotation + smallRotation;
      collision = this.checkCollisions(collidables);
      if (!collision) {
        this.tankModel.translateZ(-distance);
        collision = this.checkCollisions(collidables);
        if (collision) {
          this.tankModel.position.copy(originalPosition);
          this.tankModel.rotation.y = originalRotation;
          return true;
        }
        return false;
      }
      this.tankModel.position.copy(originalPosition);
      this.tankModel.rotation.y = originalRotation;
      return true;
    }
    return false;
  }

  setSkin(skinName: string) {
    if (!this.tankModel) return;
    const texture = getTextureForSkin(skinName) as any;
    if (!texture) return;
    applySkinToObject(this.tankModel, texture);
    this.textureMap = texture as any;
  }

  setName(name: string) {
    if (this.tankModel) {
      (this.tankModel as any).userData.info = (this.tankModel as any).userData.info || {};
      (this.tankModel as any).userData.info.name = name;
    }
  }
}

/* eslint-disable unicode-bom */
